import { CheckIcon, Loader, XIcon } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { IFieldContext } from "../../../constants";
import { Button } from "../../Button";
import Flex from "../../Flex";
import { BaseDropdown, IBaseDropdownProps } from "../BaseDropdown";
import { BaseDropdownRef } from "../BaseDropdown/BaseDropdown";
import { IFieldProps, withField } from "../Field";
import FitInput from "../FitInput/FitInput";
import * as S from "./styled";
import { ISelectOption } from "./types";

const DEFAULT_GROUP = "__default__";
type TSelectValue = string | number | boolean | object;

export interface ISelectProps<
    V extends TSelectValue,
    L extends string = string,
    T extends string = string,
    G extends string = string
> extends IFieldContext,
        Partial<IBaseDropdownProps> {
    value?: V;
    controlled?: boolean;
    name?: T;
    options: ISelectOption<V, L>[];
    optionGroups?: G[];
    onBlur?: () => void;
    onChange: (value?: V) => void;
    onSearch?: (newSearch: string) => void;
    pattern?: RegExp;
    disabled?: boolean;
    loading?: boolean;
    trackName: string;
    trackData?: object;
    placeholder?: string;
    children?: (option: ISelectOption<V, L>, place: "dropdown" | "input") => JSX.Element;
    noResults?: React.ReactNode;
    filterable?: boolean;
    filterOptions?: (options: ISelectOption<V, L>[], search: string) => ISelectOption<V, L>[];
    getOptionKey?: (option: ISelectOption<V, L>) => React.Key;
    isOptionEqualToValue?: (optionValue: V, currentValue: V | undefined) => boolean;
}

const renderOptionDefault = (option: ISelectOption) => {
    return <span>{option.label}</span>;
};

const Select = <
    V extends TSelectValue,
    L extends string = string,
    T extends string = string,
    G extends string = string
>({
    value,
    controlled,
    name,
    options,
    optionGroups,
    onBlur,
    onChange,
    onSearch,
    pattern,
    disabled = false,
    loading = false,
    placeholder,
    children = renderOptionDefault,
    noResults,
    filterable = true,
    withClearButton = false,
    trail,
    filterOptions = (filterableOptions, filterableSearch) =>
        filterableOptions.filter(option =>
            option.label.toLocaleLowerCase().includes(filterableSearch.toLocaleLowerCase())
        ),
    getOptionKey = option => `${option.value}_${option.label}`,
    isOptionEqualToValue = (optionValue, currentValue) => optionValue === currentValue,
    ...props
}: ISelectProps<V, L, T, G>): React.JSX.Element => {
    const [selectedValue, setInternalSelectedValue] = React.useState<V | undefined>(value);
    const [search, setSearch] = React.useState<string>("");
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dropdownRef = React.useRef<BaseDropdownRef>(null);

    const filteredList = useMemo<ISelectOption<V, L>[]>(
        () => (search !== "" ? filterOptions(options, search) : options),
        [search, options, filterOptions]
    );

    const setSelectedOption = useCallback(
        (newOption?: ISelectOption<V, L>) => {
            if (controlled) {
                onChange(newOption?.value);
            } else {
                setInternalSelectedValue(newOption?.value);
            }
        },
        [controlled, onChange]
    );

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (pattern && pattern.test(e.target.value)) {
            setSearch(e.target.value);
            onSearch && onSearch(e.target.value);
            return;
        }

        if (!pattern) {
            setSearch(e.target.value);
            onSearch && onSearch(e.target.value);
        }
    };

    const handleSelectOption = (newValue?: ISelectOption<V, L>) => {
        const foundType: ISelectOption<V, L> | undefined = options.find(option => option.value === newValue?.value);

        if (foundType) {
            if (selectedValue === foundType.value) {
                setSelectedOption(undefined);
            } else {
                setSelectedOption(foundType);
                dropdownRef.current?.close();
            }
        }
        setSearch("");
        onSearch && onSearch("");
    };

    React.useEffect(() => {
        if (!controlled) {
            onChange(selectedValue);
        }
    }, [selectedValue]);

    React.useEffect(() => {
        if (controlled) {
            setInternalSelectedValue(value);
        }
    }, [value]);

    const groupedFilteredList = React.useMemo<{ [key: string]: ISelectOption<V, L>[] }>(() => {
        return filteredList.reduce((acc, cur) => {
            const id = cur.group ?? DEFAULT_GROUP;
            const group = acc[id] || [];
            group.push(cur);

            return {
                ...acc,
                [id]: group
            };
        }, {});
    }, [filteredList]);

    const selectedOption = React.useMemo<ISelectOption<V, L> | undefined>(
        () => options.find(option => isOptionEqualToValue(option.value, selectedValue)),
        [selectedValue, options, isOptionEqualToValue]
    );

    return (
        <BaseDropdown
            ref={dropdownRef}
            disabled={disabled}
            onDropdownOpen={() => {
                inputRef.current?.focus();
            }}
            onDropdownClose={() => {
                setSearch("");
                onSearch && onSearch("");

                /**
                 * Note: pushing onBlur at the bottom of the call stack prevents it from running
                 * between the moments when an option is selected but the value is not updated yet,
                 * aligning the behavior with the native select component.
                 */
                onBlur && setTimeout(onBlur, 0);
            }}
            trail={trail}
            clear={
                withClearButton &&
                selectedValue && (
                    <S.ClearWrapper>
                        <Button
                            icon={<XIcon />}
                            onClick={e => {
                                e.preventDefault();
                                setInternalSelectedValue(undefined);
                            }}
                            trackName="clearInput"
                            variant="tertiary"
                        />
                    </S.ClearWrapper>
                )
            }
            withClearButton={withClearButton}
            {...props}
            dropdownContent={
                <>
                    {Object.keys(groupedFilteredList).map(group => (
                        <React.Fragment key={group}>
                            {groupedFilteredList[group] ? (
                                <>
                                    {optionGroups && optionGroups.length > 1 && S.OptionsGroup && (
                                        <S.OptionsGroup>{group}</S.OptionsGroup>
                                    )}
                                    {groupedFilteredList[group].map(option => (
                                        <S.DropdownItem
                                            key={getOptionKey(option)}
                                            isActive={selectedValue === option.value}
                                            onClick={() => handleSelectOption(option)}
                                        >
                                            <Flex
                                                gap={8}
                                                width="100%"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                {children(option, "dropdown")}
                                                {selectedValue === option.value && <CheckIcon size={20} />}
                                            </Flex>
                                        </S.DropdownItem>
                                    ))}
                                </>
                            ) : null}
                        </React.Fragment>
                    ))}
                    {filteredList.length === 0 && noResults && <S.DropdownItem>{noResults}</S.DropdownItem>}
                </>
            }
        >
            <S.ContentWrapper>
                {selectedOption && <S.SelectedOption>{children(selectedOption, "input")}</S.SelectedOption>}

                {filterable && (
                    <FitInput
                        ref={inputRef}
                        disabled={disabled}
                        name={name}
                        onChange={handleSearch}
                        placeholder={selectedValue ? undefined : placeholder}
                        value={search}
                    />
                )}
                {loading && <Loader />}
            </S.ContentWrapper>
        </BaseDropdown>
    );
};

export default withField(Select) as {
    <V extends TSelectValue, L extends string, T extends string, G extends string>(
        props: ISelectProps<V, L, T, G> & IFieldProps
    ): React.JSX.Element;
};
